import type { Theme } from 'react-select'

/**
 * Metronic palette, taken as CSS variables rather than resolved colours.
 *
 * Every `--bs-*` below is redefined by Metronic under `[data-bs-theme='dark']`
 * (see `_metronic/assets/sass/core/components/_root.scss`), so the same value
 * renders light or dark depending on the attribute on `<html>`. Values mirror
 * Metronic's `.form-control` / `.form-select` and `.dropdown-menu`, so a
 * react-select control looks like the native form fields next to it.
 */
const COLOR = {
  surface: 'var(--bs-body-bg)',
  disabledBg: 'var(--bs-gray-200)',
  border: 'var(--bs-gray-300)',
  borderHover: 'var(--bs-gray-400)',
  chipBg: 'var(--bs-gray-300)',
  muted: 'var(--bs-gray-500)',
  icon: 'var(--bs-gray-500)',
  iconHover: 'var(--bs-gray-700)',
  text: 'var(--bs-gray-700)',
  textStrong: 'var(--bs-gray-800)',
  menuBg: 'var(--bs-dropdown-bg)',
  hoverBg: 'var(--bs-component-hover-bg)',
  activeBg: 'var(--bs-component-active-bg)',
  activeText: 'var(--bs-component-active-color)',
  activePressedBg: 'var(--bs-primary-active)',
  danger: 'var(--bs-danger)',
  dangerLight: 'var(--bs-danger-light)',
}

/**
 * The admin panel's palette in react-select's own `theme` prop — the library's
 * documented way to recolour a select. Every internal element derives its colour
 * from these slots, so the whole widget is themed by one mapping instead of an
 * override per element.
 *
 * The palette is expressed in theme-aware CSS variables, so the mapping is the
 * same for both themes: the browser re-resolves the variables when
 * `data-bs-theme` flips on `<html>`, and colours follow the theme switch with no
 * React state, no `useThemeMode()` and no re-render.
 *
 * Only colours are mapped — `spacing` and `borderRadius` stay at react-select
 * defaults, so theming a select never shifts an existing layout.
 *
 * Slot → element (react-select `src/theme.ts` + the style functions):
 * - `neutral0` control and menu background, and the label of a selected option
 * - `neutral5` / `neutral10` disabled control background / disabled border and chips
 * - `neutral20` control border, idle indicators, disabled option label
 * - `neutral30` control border on hover
 * - `neutral40` group heading, "no options", disabled value
 * - `neutral50` placeholder
 * - `neutral60`…`neutral90` indicator on focus and the value / input text
 * - `primary` focused border and selected option, `primary25` hovered option,
 *   `primary50` option being pressed
 */
export const selectTheme = (base: Theme): Theme => ({
  ...base,
  colors: {
    ...base.colors,
    neutral0: COLOR.surface,
    neutral5: COLOR.disabledBg,
    neutral10: COLOR.chipBg,
    neutral20: COLOR.border,
    neutral30: COLOR.borderHover,
    neutral40: COLOR.muted,
    neutral50: COLOR.muted,
    neutral60: COLOR.icon,
    neutral70: COLOR.text,
    neutral80: COLOR.text,
    neutral90: COLOR.textStrong,
    primary: COLOR.activeBg,
    primary75: COLOR.activeBg,
    primary50: COLOR.activePressedBg,
    primary25: COLOR.hoverBg,
    danger: COLOR.danger,
    dangerLight: COLOR.dangerLight,
  },
})

/**
 * The three things the `theme` prop cannot express, and only those.
 *
 * Everything else is left to `selectTheme` — do not grow this object with
 * colours that a palette slot already covers.
 */
export const selectStructuralStyles = {
  // The menu has to clear Metronic modals and sticky table headers; the theme
  // has no stacking slot.
  menu: (base: any) => ({ ...base, backgroundColor: COLOR.menuBg, zIndex: 105 }),
  // `neutral20` is both the control border and the idle indicators. A tint that
  // reads as a quiet border is too faint for an icon, so arrow and clear button
  // are lifted to the icon tokens.
  dropdownIndicator: (base: any) => ({
    ...base,
    color: COLOR.icon,
    ':hover': { color: COLOR.iconHover },
  }),
  clearIndicator: (base: any) => ({
    ...base,
    color: COLOR.icon,
    ':hover': { color: COLOR.iconHover },
  }),
  // `neutral0` is both the surface colour and the label of a selected option.
  // On the primary fill the label needs the inverse token, not the surface.
  option: (base: any, { isSelected }: any) => ({
    ...base,
    color: isSelected ? COLOR.activeText : base.color,
  }),
}

/**
 * Compatibility shim for pages that still pass `styles={SelectStyles(theme)}`.
 *
 * A `styles` prop cannot reach the `theme` prop, so these call sites need the
 * palette spelled out as overrides. Values are read from the same `COLOR` map
 * and mirror what `selectTheme` produces slot for slot, so both paths render
 * identically — keep them in step if the palette changes.
 *
 * @deprecated Use `<AppSelect>`; it applies `selectTheme` and needs no styles.
 * @param _theme kept for the existing `SelectStyles(theme)` call sites; ignored.
 */
export const SelectStyles = (_theme?: string) => ({
  control: (base: any, { isDisabled, isFocused }: any) => ({
    ...base,
    backgroundColor: isDisabled ? COLOR.disabledBg : COLOR.surface,
    borderColor: isDisabled ? COLOR.chipBg : isFocused ? COLOR.activeBg : COLOR.border,
    boxShadow: 'none',
    '&:hover': { borderColor: isFocused ? COLOR.activeBg : COLOR.borderHover },
  }),
  placeholder: (base: any) => ({ ...base, color: COLOR.muted }),
  singleValue: (base: any, { isDisabled }: any) => ({
    ...base,
    color: isDisabled ? COLOR.muted : COLOR.text,
  }),
  input: (base: any) => ({ ...base, color: COLOR.text }),
  multiValue: (base: any) => ({ ...base, backgroundColor: COLOR.chipBg }),
  multiValueLabel: (base: any) => ({ ...base, color: COLOR.text }),
  multiValueRemove: (base: any, { isFocused }: any) => ({
    ...base,
    backgroundColor: isFocused ? COLOR.dangerLight : undefined,
    ':hover': { backgroundColor: COLOR.dangerLight, color: COLOR.danger },
  }),
  indicatorSeparator: (base: any, { isDisabled }: any) => ({
    ...base,
    backgroundColor: isDisabled ? COLOR.chipBg : COLOR.border,
  }),
  loadingIndicator: (base: any) => ({ ...base, color: COLOR.icon }),
  groupHeading: (base: any) => ({ ...base, color: COLOR.muted }),
  noOptionsMessage: (base: any) => ({ ...base, color: COLOR.muted }),
  loadingMessage: (base: any) => ({ ...base, color: COLOR.muted }),
  ...selectStructuralStyles,
  option: (base: any, { isDisabled, isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isSelected ? COLOR.activeBg : isFocused ? COLOR.hoverBg : 'transparent',
    color: isDisabled ? COLOR.border : isSelected ? COLOR.activeText : 'inherit',
    ':active': {
      ...base[':active'],
      backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? COLOR.activeBg
        : COLOR.activePressedBg,
    },
  }),
})
